import { useNavigate } from '@tanstack/react-router';
import { memo, useCallback } from 'react';
import { Focusable } from 'react-aria-components';

import { Button, Heading, Logo, Menu, MenuItem, Text, Tooltip, TooltipTrigger, TruncatedText } from '@/components/core';
import { CheckListIcon, ChevronIcon, EmailIcon } from '@/components/icons';
import { NavigationBar } from '@/components/ui';
import { useAgentRunTree, useGetAgentFromPath, useGetAgentRunFromPath } from '@/hooks';

import { useOpenPanel } from './useOpenPanel.ts';

export type DetailsNavigationBarProps = object;

// eslint-disable-next-line no-empty-pattern
const IDetailsNavigationBar = ({}: DetailsNavigationBarProps = {}) => {
  const { data: run } = useGetAgentRunFromPath();
  const { data: agent } = useGetAgentFromPath();
  const navigate = useNavigate();
  const { agentRunsPanelOpen, emailPanelOpen } = useOpenPanel();
  const { taskDocument, emailDocument } = useAgentRunTree({ agentId: run?.agentId, runId: run?.runId });

  const onBack = useCallback(() => {
    if (agent) {
      navigate({ to: '/agents/$agentId', params: { agentId: agent.agentId } }).catch(console.error);
    }
  }, [agent, navigate]);

  const onOpenRuns = useCallback(() => {
    if (run) {
      navigate({
        to: '/details/agents/$agentId/runs/$runId',
        params: { agentId: run.agentId, runId: run.runId },
        search: agentRunsPanelOpen ? undefined : { showPanel: 'agent-runs' },
      }).catch(console.error);
    }
  }, [run, agentRunsPanelOpen, navigate]);

  const onOpenEmail = useCallback(() => {
    if (run) {
      navigate({
        to: '/details/agents/$agentId/runs/$runId',
        params: { agentId: run.agentId, runId: run.runId },
        search: emailPanelOpen ? undefined : { showPanel: 'email' },
      }).catch(console.error);
    }
  }, [run, emailPanelOpen, navigate]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="py-sm px-xs flex h-14 w-full items-center border-b border-gray-200 bg-white">
        <TooltipTrigger>
          <Tooltip variant="primary">
            <Text>Go back</Text>
          </Tooltip>
          <Button onPress={onBack} variant="plain">
            <Logo size="md">
              <ChevronIcon rotation="left" />
            </Logo>
          </Button>
        </TooltipTrigger>

        <Button onPress={onBack} variant="plain">
          <Heading>Runs</Heading>
        </Button>

        <ChevronIcon rotation="right" />

        <TruncatedText className="ml-2 max-w-[25vw]">{taskDocument?.name}</TruncatedText>
      </div>
      <NavigationBar className="items-center py-2" initialWidth={48} enableResizing={false}>
        <Menu>
          <MenuItem onAction={onOpenRuns}>
            <TooltipTrigger>
              <Tooltip variant="primary">
                <Text>View runs</Text>
              </Tooltip>
              <Focusable>
                <CheckListIcon />
              </Focusable>
            </TooltipTrigger>
          </MenuItem>
          <MenuItem onAction={onOpenEmail} isDisabled={!emailDocument}>
            <TooltipTrigger>
              <Tooltip variant="primary">
                <Text>{emailDocument ? 'View original email' : 'No original email'}</Text>
              </Tooltip>
              <Focusable>
                <EmailIcon />
              </Focusable>
            </TooltipTrigger>
          </MenuItem>
        </Menu>
      </NavigationBar>
    </div>
  );
};

export const DetailsNavigationBar = memo(IDetailsNavigationBar) as typeof IDetailsNavigationBar;
